import { useQuery } from '@tanstack/react-query'
import { Pencil } from 'lucide-react'
import { ListPage, type ListPageColumn } from '@/components/templates/list-page'
import { useViewOnly } from '@/components/templates/app-shell'
import { OwnedChip, isConnectionOwned, type Owner } from '@/components/OwnedChip'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { getJSON } from '@/lib/api'
import { cn } from '@/lib/utils'
import { useCourseListState, type CourseListState } from '@/pages/course-list-state'

export interface CourseListRecord {
  id: number
  title: string
  delivered_by: string | null
  credits: number
  enrolled_count: number
  completed_count: number
  status: string
  owner: Owner | string | null
}

const STATUSES = [
  ['active', 'Active'],
  ['archived', 'Archived'],
  ['all', 'All'],
] as const

export function CoursesListPage({
  onNew,
  onOpen,
  onEdit,
  onImport,
}: {
  onNew: () => void
  onOpen: (course: CourseListRecord) => void
  onEdit: (course: CourseListRecord) => void
  onImport?: () => void
}) {
  const viewOnly = useViewOnly()
  const { state, update, toggleSort } = useCourseListState()
  const query = useQuery({
    queryKey: ['courses', state],
    queryFn: () =>
      getJSON<{ courses: CourseListRecord[] }>(
        `/api/v1/courses?${new URLSearchParams({ ...state })}`,
      ),
  })
  const owners = useQuery({
    queryKey: ['course-owners'],
    queryFn: () => getJSON<{ courses: CourseListRecord[] }>('/api/v1/courses?status=all'),
  })
  const settings = useQuery({
    queryKey: ['credit-unit-settings'],
    queryFn: () =>
      getJSON<{ credit_unit_settings: { label: string } }>('/api/v1/credit_unit_settings'),
  })
  const filtered = Boolean(state.search || state.owner || state.status !== 'active')
  return (
    <ListPage
      title="Courses"
      primaryAction={{ label: 'New course', onClick: onNew }}
      search={{
        value: state.search,
        onChange: (search) => update({ search }),
        placeholder: 'Search courses…',
      }}
      toolbar={
        <div className="flex flex-wrap items-center gap-1.5" aria-label="Filter courses">
          {STATUSES.map(([value, label]) => (
            <Button
              key={value}
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={state.status === value}
              onClick={() => update({ status: value })}
              className={cn(
                'h-9 rounded-full px-3 text-caption font-normal',
                state.status === value &&
                  'border-foreground/20 bg-accent font-medium text-foreground',
              )}
            >
              {label}
            </Button>
          ))}
          <FilterDropdown
            label="Owner"
            emptyLabel="All owners"
            options={ownerOptions(owners.data?.courses ?? [])}
            selected={new Set(state.owner ? [state.owner] : [])}
            onChange={(next) => update({ owner: Array.from(next).at(-1) ?? '' })}
          />
          {!viewOnly && onImport && (
            <Button type="button" variant="outline" size="sm" onClick={onImport}>
              Import enrolments
            </Button>
          )}
        </div>
      }
      table={{
        rows: query.data?.courses ?? [],
        state: tableState(query),
        getRowId: (course) => course.id,
        getRowLabel: (course) => course.title,
        onRowOpen: onOpen,
        emptyTitle: filtered ? 'No matching courses' : 'No courses yet.',
        emptyVariant: filtered ? 'default' : 'quiet',
        columns: courseColumns(
          state,
          toggleSort,
          settings.data?.credit_unit_settings.label ?? 'credits',
          viewOnly,
          { onEdit, onOpen },
        ),
      }}
    />
  )
}

function tableState(query: { isLoading: boolean; isError: boolean }) {
  if (query.isLoading) return 'loading'
  return query.isError ? 'error' : 'ready'
}

function ownerOptions(courses: CourseListRecord[]) {
  const options = new Map([['bright_huddle', 'Bright Huddle']])
  for (const course of courses) {
    if (isConnectionOwned(course.owner)) {
      const connection = course.owner.connection!
      options.set(String(connection.id), connection.display_name)
    }
  }
  return Array.from(options, ([value, label]) => ({ value, label }))
}

function courseColumns(
  state: CourseListState,
  toggleSort: (sort: string) => void,
  unit: string,
  viewOnly: boolean,
  actions: {
    onEdit: (course: CourseListRecord) => void
    onOpen: (course: CourseListRecord) => void
  },
): ListPageColumn<CourseListRecord>[] {
  const { onEdit, onOpen } = actions
  const sortable = (column: string) => ({
    active: state.sort === column,
    direction: state.direction === 'asc' ? ('ascending' as const) : ('descending' as const),
    onToggle: () => toggleSort(column),
  })
  const numeric = (
    id: 'credits' | 'enrolled' | 'completed',
    header: string,
    field: 'credits' | 'enrolled_count' | 'completed_count',
  ) => ({
    id,
    header,
    sort: sortable(id),
    headerClassName: 'text-right',
    className: cn('text-right tabular-nums', id === 'completed' && 'font-semibold'),
    cell: (course: CourseListRecord) => course[field],
  })
  return [
    {
      id: 'title',
      header: 'Title',
      sort: sortable('title'),
      cell: (course) => (
        <div className="flex items-center gap-2 font-medium">
          <a
            href={`/courses/${course.id}`}
            className="underline-offset-4 hover:underline focus-visible:underline"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              onOpen(course)
            }}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {course.title}
          </a>
          <OwnedChip owner={course.owner} />
        </div>
      ),
    },
    {
      id: 'delivered_by',
      header: 'Delivered by',
      sort: sortable('delivered_by'),
      className: 'text-muted-foreground',
      cell: (course) => course.delivered_by,
    },
    numeric('credits', unit.charAt(0).toUpperCase() + unit.slice(1), 'credits'),
    numeric('enrolled', 'Enrolled', 'enrolled_count'),
    numeric('completed', 'Completed', 'completed_count'),
    {
      id: 'status',
      header: 'Status',
      cell: (course) => (
        <Badge variant={course.status === 'active' ? 'success' : 'neutral'}>
          {course.status === 'active' ? 'Active' : 'Archived'}
        </Badge>
      ),
    },
    {
      id: 'edit',
      header: <span className="sr-only">Edit</span>,
      headerClassName: 'w-12',
      className: 'w-12 py-1 text-right',
      cell: (course) => <CourseEdit course={course} viewOnly={viewOnly} onEdit={onEdit} />,
    },
  ]
}

function CourseEdit({
  course,
  viewOnly,
  onEdit,
}: {
  course: CourseListRecord
  viewOnly: boolean
  onEdit: (course: CourseListRecord) => void
}) {
  if (viewOnly || isConnectionOwned(course.owner)) return null
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={`Edit ${course.title}`}
      title={`Edit ${course.title}`}
      onClick={(event) => {
        event.stopPropagation()
        onEdit(course)
      }}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
    </Button>
  )
}
